import {FC, memo, useCallback, useEffect, useState} from 'react'
import {Col, Modal, Row, Form as BSForm} from 'react-bootstrap'
import {KTSVG, toAbsoluteUrl} from '../../../../../_metronic/helpers'
import {
  CreateSubClient,
  SubClient,
  UpdateSubClientData,
} from '../../../../../services/cellcast.v2.types'
import {ErrorMessage, Field, Form, Formik} from 'formik'
import {object, string} from 'yup'
import {
  IndustryOptions,
  SelectFeature,
} from '../../../AdminPages/AllCellCastUser/Constant/CellCastConstant'
import Select from 'react-select'
import {
  useCreateSubClientMutation,
  useUpdateSubClientMutation,
} from '../../../../../services/cellcast.v2'
import {toast} from 'react-toastify'
import './WriteUser.css'
import { getAllUserDetails } from '../../accounts/Overview/OverviewSlice/UserDetailsSlice'
import { GLOBAL_SETTINGS_TYPE } from '../../../../Utils/Constant/ConstantForWebsite'
import { useSelector } from 'react-redux'

// validation schema
const userSchema = object().shape({
  email: string().email('Wrong email format').required('Email is required'),
  mobile: string().min(9).max(12),
  name: string()
    .max(25, 'Input exceeds the maximum character length of 25')
    .required('Full name is required')
    .trim('The contact name cannot include leading and trailing spaces'),

  password: string().when('isEdit', {
    is: false,
    then: string()
      .min(8, 'Must be at least 8 characters')
      .max(50, 'Must be less than 50 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
        'Minimum eight characters, at least one uppercase & lowercase letter, one number and one special character'
      )
      .required('Password is required'),
  }),
  companyName: string()
    .max(25, 'Input exceeds the maximum character length of 25')
    .required('Company name is required')
    .trim('The contact name cannot include leading and trailing spaces'),
  industry: string().required('Industry is required'),
})

interface CombinedClientData extends CreateSubClient, UpdateSubClientData {
  isEdit: boolean
}
export enum WriteUserMode {
  ADD,
  UPDATE,
}

export type WriteUserProps = {
  show: boolean
  mode: WriteUserMode
  handleClose: () => void
  callbackFn: () => void
  selectedData?: SubClient
}

const WriteUser: FC<WriteUserProps> = ({show, handleClose, selectedData, mode, callbackFn}) => {
  const userData: any = useSelector(getAllUserDetails)
  const newSubClientAutoVerify = userData?.globalSetting?.find(
      (item: string) =>
        item === GLOBAL_SETTINGS_TYPE.NEW_SUB_CLIENT_AUTO_VERIFY
    )

  const isAdmin = localStorage.getItem('isAdmin')
  // local state
  const [user, setUserData] = useState<CombinedClientData>({
    customLogo: '',
    avatar: '',
    email: '',
    mobile: '',
    name: '',
    website: '',
    password: '',
    accountManager: '',
    accountType: 'prepaid',
    sales: '',
    companyName: '',
    industry: '',
    features: [],
    notes: '',
    isSharingCredit: false,
    isEmailVerified: newSubClientAutoVerify ? true : false,
    isEdit: mode === WriteUserMode.UPDATE,
  })
  const [showPassword, setShowPassword] = useState(false)

  // create and update mutation hook
  const [create, {isLoading: createLoading}] = useCreateSubClientMutation()
  const [update, {isLoading: updateLoading}] = useUpdateSubClientMutation()

  // handler
  const handleSubmit = useCallback(
    (params: CombinedClientData) => {
      const formData = new FormData()

      const tmpParams: any = {...params}
      if (params.isEdit) {
        delete tmpParams.password
        delete tmpParams.isEdit
      }

      Object.entries(tmpParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === "features" && Array.isArray(value)) {
              formData.append(key, value.join(","))
            } else {
              formData.append(key, value as any)
            }
        }
      })

      if (params.isEdit) {
        // const p: UpdateSubClientData = {...tmpParams}
        update({
          id: selectedData ? selectedData._id : '',
          data: formData,
        })
          .then((res) => {
            if ('data' in res) {
              toast.success(res.data?.message)

              if (callbackFn) {
                callbackFn()
              }
            } else if ('error' in res) {
              const errObj = res.error as any
              if (errObj.data.message) {
                toast.error(errObj.data.message)
              } else {
                toast.error(res.error)
              }
            }
          })
          .catch((err) => {
            toast.error(err.error)
          })
      } else {
        create(formData)
          .then((res) => {
            if ('data' in res) {
              toast.success(res.data?.message)

              if (callbackFn) {
                callbackFn()
              }
            } else if ('error' in res) {
              const errObj = res.error as any
              if (errObj.data.message) {
                toast.error(errObj.data.message)
              } else {
                toast.error(res.error)
              }
            }
          })
          .catch((err) => {
            toast.error(err.error)
          })
      }
    },
    [callbackFn, selectedData]
  )

  useEffect(() => {
    setShowPassword(true)
  }, [show])

  useEffect(() => {
    if (selectedData && selectedData._id) {
      setUserData({
        customLogo: selectedData.customLogo,
        avatar: selectedData.avatar,
        email: selectedData.email,
        mobile: selectedData.mobile,
        name: selectedData.name,
        website: selectedData.companyWebsite,
        password: '',
        accountManager: selectedData.accountManager,
        accountType: selectedData.accountType,
        sales: selectedData.sales,
        companyName: selectedData.companyName,
        industry: selectedData.industry,
        features: selectedData.features,
        notes: selectedData.notes,
        isSharingCredit: selectedData._id.toString() !== selectedData.ownerId.toString(),
        isEdit: true,
        isEmailVerified: selectedData.isEmailVerified,
      })
    }
  }, [selectedData])

  return (
    <Modal
      id='kt_modal_create_app'
      tabIndex={1}
      aria-hidden='true'
      dialogClassName='modal-dialog-centered mw-700px'
      className='vh-100'
      show={show}
      onHide={handleClose}
      scrollabel={'true'}
    >
      <div className='modal-header'>
        <h2>{`${
          mode === WriteUserMode.ADD ? 'Add New Sub Client Account' : 'Update Sub Client Account'
        }`}</h2>
        <div className='btn btn-icon btn-sm' onClick={handleClose}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>
      <div className='modal-body px-20 py-5'>
        <Formik<CombinedClientData>
          initialValues={user}
          onSubmit={(value, action) => {
            handleSubmit(value)
            action.setSubmitting(false)
          }}
          validationSchema={userSchema}
        >
          {({values, setFieldValue, handleChange}) => (
            <Form>
              <div className='d-flex gap-8'>
                <div className='row mb-6'>
                  <label className='col-form-label fw-bold fs-6 ms-3'>Avatar</label>
                  <div
                    className='image-input image-input-outline w-auto ms-3'
                    data-kt-image-input='true'
                  >
                    {typeof values.avatar !== 'object' && values.avatar !== null ? (
                      <img
                        src={
                          values.avatar === ''
                            ? toAbsoluteUrl('/media/avatars/blank.png')
                            : values.avatar
                        }
                        alt='poster'
                        width={125}
                        height={125}
                      />
                    ) : (
                      <img
                        src={typeof values.avatar === 'string' ? values.avatar : URL.createObjectURL(values.avatar)}
                        alt='poster'
                        width={125}
                        height={125}
                      />
                    )}

                    {!createLoading && !updateLoading && (
                      <>
                        <label
                          className='btn btn-icon btn-circle btn-color btn-active-color-primary w-25px h-25px bg-body shadow'
                          data-kt-image-input-action='change'
                          data-bs-toggle='tooltip'
                          data-bs-dismiss='click'
                          title='Change avatar'
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='16'
                            height='16'
                            fill='currentColor'
                            className='bi bi-pencil-fill'
                            viewBox='0 0 16 16'
                          >
                            <path d='M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z' />
                          </svg>
                          <input
                            type='file'
                            name='avatar'
                            onChange={(event: any) => setFieldValue('avatar', event?.target.files[0])}
                            accept='.png, .jpg, .jpeg'
                          />
                        </label>

                        <span
                          className='btn btn-icon btn-circle btn-color btn-active-color-primary w-25px h-25px bg-body shadow'
                          data-kt-image-input-action='remove'
                          data-bs-toggle='tooltip'
                          data-bs-dismiss='click'
                          title='Remove avatar'
                          onClick={(event: any) => setFieldValue('avatar', '')}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='25'
                            height='25'
                            fill='currentColor'
                            className='bi bi-x'
                            viewBox='0 0 16 16'
                          >
                            <path d='M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z' />
                          </svg>
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-form-label fw-bold fs-6 ms-3'>Custom Logo</label>
                  <div
                    className='image-input image-input-outline w-auto ms-3'
                    data-kt-image-input='true'
                  >
                    {typeof values.customLogo !== 'object' && values.customLogo !== null ? (
                      <img
                        src={
                          !values.customLogo
                            ? toAbsoluteUrl('/media/avatars/blank.png')
                            : values.customLogo
                        }
                        alt='Logo'
                        width={125}
                        height={125}
                      />
                    ) : (
                      <img
                        src={typeof values.customLogo === 'string' ? values.customLogo : URL.createObjectURL(values.customLogo)}
                        alt='Logo'
                        width={125}
                        height={125}
                      />
                    )}

                    {!createLoading && !updateLoading && (
                      <>
                        <label
                          className='btn btn-icon btn-circle btn-color btn-active-color-primary w-25px h-25px bg-body shadow'
                          data-kt-image-input-action='change'
                          data-bs-toggle='tooltip'
                          data-bs-dismiss='click'
                          title='Change avatar'
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='16'
                            height='16'
                            fill='currentColor'
                            className='bi bi-pencil-fill'
                            viewBox='0 0 16 16'
                          >
                            <path d='M12.854.146a.5.5 0 0 0-.707 0L10.5 1.793 14.207 5.5l1.647-1.646a.5.5 0 0 0 0-.708l-3-3zm.646 6.061L9.793 2.5 3.293 9H3.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.207l6.5-6.5zm-7.468 7.468A.5.5 0 0 1 6 13.5V13h-.5a.5.5 0 0 1-.5-.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.5-.5V10h-.5a.499.499 0 0 1-.175-.032l-.179.178a.5.5 0 0 0-.11.168l-2 5a.5.5 0 0 0 .65.65l5-2a.5.5 0 0 0 .168-.11l.178-.178z' />
                          </svg>
                          <input
                            type='file'
                            name='customLogo'
                            onChange={(event: any) => setFieldValue('customLogo', event?.target.files[0])}
                            accept='.png, .jpg, .jpeg'
                          />
                        </label>

                        <span
                          className='btn btn-icon btn-circle btn-color btn-active-color-primary w-25px h-25px bg-body shadow'
                          data-kt-image-input-action='remove'
                          data-bs-toggle='tooltip'
                          data-bs-dismiss='click'
                          title='Remove avatar'
                          onClick={(event: any) => setFieldValue('customLogo', '')}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='25'
                            height='25'
                            fill='currentColor'
                            className='bi bi-x'
                            viewBox='0 0 16 16'
                          >
                            <path d='M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z' />
                          </svg>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <ErrorMessage name='avatar'>
                {(msg: string) => <span className='text-danger'>{msg}</span>}
              </ErrorMessage>

              <Row>
                <Col md={12}>
                  <BSForm.Group>
                    <BSForm.Label htmlFor='name' className=' required'>
                      Name
                    </BSForm.Label>
                  </BSForm.Group>
                  <Field
                    id={'name'}
                    className={'form-control form-control-solid  mb-3 '}
                    name='name'
                    type='text'
                    onChange={handleChange}
                    disabled={createLoading || updateLoading}
                  />
                  <ErrorMessage name='name' component='span' className='text-danger' />
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <BSForm.Group>
                    <BSForm.Label htmlFor='companyName' className='required'>
                      Company
                    </BSForm.Label>
                    <Field
                      id={'companyName'}
                      className={'form-control  form-control-solid'}
                      name='companyName'
                      type='text'
                      onChange={handleChange}
                      disabled={createLoading || updateLoading}
                    />
                    <ErrorMessage name='companyName' component='span' className='text-danger' />
                  </BSForm.Group>
                </Col>
              </Row>

              <Row>
                <Col lg={6} md={6}>
                  <BSForm.Group>
                    <BSForm.Label className='required' htmlFor='email'>
                      Email
                    </BSForm.Label>
                    <Field
                      id={'email-user'}
                      className={'form-control form-control-lg form-control-solid mb-3 mb-lg-0'}
                      name='email'
                      type='text'
                      onChange={handleChange}
                      disabled={createLoading || updateLoading}
                    />
                    <ErrorMessage name='email' component='span' className='text-danger' />
                  </BSForm.Group>
                </Col>
                <Col lg={6} md={6}>
                  <BSForm.Label htmlFor='mobile-user'>Phone</BSForm.Label>
                  <Field
                    id={'mobile-user'}
                    className={'form-control form-control-lg form-control-solid mb-3 mb-lg-0'}
                    name='mobile'
                    type='text'
                    disabled={createLoading || updateLoading}
                  />
                  <ErrorMessage name='mobile' component='span' className='text-danger' />
                </Col>
              </Row>

              <Row>
                <Col lg={6} md={6}>
                  <BSForm.Group>
                    <BSForm.Label htmlFor='website-user'>Website</BSForm.Label>
                    <Field
                      id={'website-user'}
                      className={'form-control form-control-lg form-control-solid mb-3 mb-lg-0'}
                      name='website'
                      type='text'
                      disabled={createLoading || updateLoading}
                    />
                    <ErrorMessage name='website' component='span' className='text-danger' />
                  </BSForm.Group>
                </Col>
                <Col lg={6} md={6}>
                  <BSForm.Group>
                    <BSForm.Label className='required' htmlFor='industry'>
                      Industry
                    </BSForm.Label>
                    <Select
                      options={IndustryOptions}
                      value={{value: values?.industry, label: values?.industry}}
                      onChange={(e: any) => setFieldValue('industry', e.value)}
                      isDisabled={createLoading || updateLoading}
                    />
                    <ErrorMessage name='industry' component='span' className='text-danger' />
                  </BSForm.Group>
                </Col>
              </Row>

              {mode === WriteUserMode.ADD && (
                <Col lg={12} md={12}>
                  <BSForm.Group>
                    <BSForm.Label className='required' htmlFor='password-user'>
                      Password
                    </BSForm.Label>
                    <Field
                      id={'password-user'}
                      className={'form-control form-control-solid mb-3 mb-lg-0'}
                      name='password'
                      type={showPassword ? 'password' : 'text'}
                      disabled={createLoading || updateLoading}
                    />
                    <ErrorMessage name='password' component='span' className='text-danger' />
                  </BSForm.Group>
                </Col>
              )}

              {isAdmin === 'true' ? (
                <Row className='mb-6'>
                  <Col mb={12}>
                    <BSForm.Group>
                      <BSForm.Label>Feature</BSForm.Label>
                      <Row>
                        {SelectFeature?.map((key: any, index: any) => {
                          return (
                            <div
                              key={index}
                              className='col-6 form-check form-check-custom form-check-solid mt-2 px-3'
                            >
                              <input
                                className='form-check-input'
                                type='checkbox'
                                id='inlineCheckbox1'
                                name='features'
                                value={key}
                                checked={values.features?.includes(key)}
                                onChange={(e: any) => {
                                  if (values.features?.includes(e.target.value)) {
                                    let data: string[] = []
                                    if (Array.isArray(values.features)) {
                                      data = [...values.features]
                                    }
                                    let index = values.features?.indexOf(e.target.value)

                                    data?.splice(index, 1)
                                    setFieldValue('features', data)
                                  } else {
                                    let data: string[] = []
                                    if (Array.isArray(values.features)) {
                                      data = [...values.features]
                                    }
                                    setFieldValue('features', [...data, e.target.value])
                                  }
                                }}
                                disabled={createLoading || updateLoading}
                              />
                              <label className='form-check-label fw-bold' htmlFor='inlineCheckbox1'>
                                {key}
                              </label>
                            </div>
                          )
                        })}
                      </Row>
                    </BSForm.Group>
                  </Col>
                </Row> 
              ) : null}

              <Row>
                <Col md={12}>
                  <BSForm.Group as={Row} className='mb-3'>
                    <Col className='d-flex flex-column justify-content-center'>
                      <BSForm.Label htmlFor='isSharingCredit' className='text-muted'>
                        Sharing Credit
                      </BSForm.Label>
                    </Col>
                    <Col>
                      <BSForm.Check
                        type='switch'
                        id='isSharingCredit'
                        label={values.isSharingCredit ? 'yes' : 'no'}
                        checked={values.isSharingCredit ?? newSubClientAutoVerify}
                        onChange={handleChange}
                        name='isSharingCredit'
                        className={`form-control ${!values.isSharingCredit && 'checkbox-custom'}`}
                        disabled={createLoading || updateLoading}
                        // disabled={true}
                      />
                    </Col>
                  </BSForm.Group>
                  <ErrorMessage name='isSharingCredit' component='span' className='text-danger' />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <BSForm.Group as={Row} className='mb-3'>
                    <Col className='d-flex flex-column justify-content-center'>
                      <BSForm.Label htmlFor='isEmailVerified' className='text-muted'>
                        Email Verified
                      </BSForm.Label>
                    </Col>
                    <Col>
                      <BSForm.Check
                        type='switch'
                        id='isEmailVerified'
                        label={values.isEmailVerified ? 'yes' : 'no'}
                        checked={values.isEmailVerified ?? newSubClientAutoVerify}
                        onChange={handleChange}
                        name='isEmailVerified'
                        className={`form-control ${!values.isEmailVerified && 'checkbox-custom'}`}
                        disabled={createLoading || updateLoading}
                        // disabled={true}
                      />
                    </Col>
                  </BSForm.Group>
                  <ErrorMessage name='isEmailVerified' component='span' className='text-danger' />
                </Col>
              </Row>              

              <Row>
                <Col xs={12}>
                  <BSForm.Group controlId='notes'>
                    <BSForm.Label className='fw-bold fs-6'>Notes</BSForm.Label>
                    <Field
                      id={'notes'}
                      className={'form-control form-control-lg form-control-solid mb-3 mb-lg-0'}
                      name='notes'
                      type='text'
                      disabled={createLoading || updateLoading}
                    />
                    <ErrorMessage name='notes' component='span' className='text-danger' />
                  </BSForm.Group>
                </Col>
              </Row>

              <div className='modal-footer d-flex justify-content-center'>
                <button
                  className='btn btn-md gray_bg_purple'
                  type='button'
                  onClick={handleClose}
                  disabled={createLoading || updateLoading}
                >
                  Cancel
                </button>

                <button type='submit' className='btn btn-md btn-primary ml-2'>
                  {createLoading || updateLoading ? 'Loading....' : 'Save'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default memo(WriteUser)
